// Spec 044 slice 4a: everything about the Students list that is a pure function
// of data — the filter and sort catalogue (D21), the query a pair needs, and the
// text in every cell (D22). Jest can load this; nothing here touches Firestore.
import { toDate } from "./ticketPresentation";
import { whenLabel } from "./queuePresentation";
import { countText, dateTimeLines } from "./kitPresentation";
import { sessionStatusChip } from "./studentDetail";

export { sessionsLabel } from "./studentDetail";

export const PAGE_SIZES = [25, 50];
const DAY_MS = 24 * 60 * 60 * 1000;
const daysAgo = (now, n) => new Date(now.getTime() - n * DAY_MS);

// One filter at a time (D21). `where` returns [field, op, value] triples the
// data layer spreads into Firestore where(); `order` is set only where Firestore
// forces it — an inequality filter must order by its own field — and those two
// are the exception to "a filter pins newest-first". Every pair below has a
// composite index in the app repo's firestore.indexes.json.
export const STUDENT_FILTERS = [
  { key: "", label: "All students" },
  { key: "disabled", label: "Disabled", where: () => [["disabled", "==", true]] },
  { key: "children", label: "Has children", where: () => [["childCount", ">", 0]], order: ["childCount", "desc"] },
  { key: "zero", label: "Zero balance", where: () => [["WalletBalance", "==", 0]] },
  { key: "free", label: "Free session unused", where: () => [["hasFreeSession", "==", true]] },
  {
    key: "idle30",
    label: "No lesson in 30 days",
    where: (now) => [["lessonSummary.lastAt", "<=", daysAgo(now, 30)]],
    order: ["lessonSummary.lastAt", "desc"],
  },
  { key: "never", label: "Never booked", where: () => [["lessonSummary.count", "==", 0]] },
  // 059 D13–D17: contactSummary.count is the server's, on every student (0 when none).
  { key: "notContacted", label: "Not contacted", where: () => [["contactSummary.count", "==", 0]] },
  { key: "contacted", label: "Contacted", where: () => [["contactSummary.count", ">", 0]], order: ["contactSummary.count", "desc"] },
  // 057 D17–D19: client.appVersion is written by the 2.0.x app; a student without `client`
  // is in neither chip (Firestore cannot query a missing field). `latest` is the highest
  // version among students (getLatestAppVersion), unknown until it lands, so the chips wait.
  // ponytail: versions compare as strings, fine until a part reaches two digits ("2.0.10" <
  // "2.0.9"); a numeric client.versionCode is the upgrade.
  { key: "appLatest", label: "Latest app", needsLatest: true, where: (now, latest) => [["client.appVersion", "==", latest]] },
  {
    key: "appOlder",
    label: "Older app",
    needsLatest: true,
    where: (now, latest) => [["client.appVersion", "<", latest]],
    order: ["client.appVersion", "desc"],
  },
  { key: "joined7", label: "Joined in 7 days", where: (now) => [["createdAt", ">=", daysAgo(now, 7)]] },
  { key: "joined30", label: "Joined in 30 days", where: (now) => [["createdAt", ">=", daysAgo(now, 30)]] },
];

// Sorts apply to the unfiltered list only. A document missing the order field
// is excluded by Firestore, not sorted last: `name` and `createdAt` are set at
// signup, and `lessonSummary.lastAt` is always present (null when no lesson,
// which sorts after every date) once the backfill has run.
export const STUDENT_SORTS = [
  { key: "newest", label: "Newest", order: ["createdAt", "desc"] },
  { key: "name", label: "Name", order: ["name", "asc"] },
  { key: "lastLesson", label: "Last lesson", order: ["lessonSummary.lastAt", "desc"] },
];

const NEWEST = ["createdAt", "desc"];

// The query for a (filter, sort) pair. No second order key: the page cursor is
// a document snapshot, and Firestore breaks ties on the document id itself.
// 057 D14: a `subject` is its own view (role + interests array-contains + createdAt desc, the one
// shape indexed), so it wins over filter and sort. A needsLatest filter with no `latest` yet
// queries as All.
export const studentQuerySpec = ({ filter = "", sort = "newest", subject = "", latest = null, now = new Date() } = {}) => {
  if (subject) return { where: [["role", "==", "student"], ["interests", "array-contains", subject]], orderBy: NEWEST };
  let f = STUDENT_FILTERS.find((x) => x.key === filter) || STUDENT_FILTERS[0];
  if (f.needsLatest && !latest) f = STUDENT_FILTERS[0];
  const s = STUDENT_SORTS.find((x) => x.key === sort) || STUDENT_SORTS[0];
  return {
    where: [["role", "==", "student"], ...(f.where ? f.where(now, latest) : [])],
    orderBy: f.key ? f.order || NEWEST : s.order,
  };
};

// Search goes to the Ctrl+K people index (exact phone, prefix name): one
// character would match half the list five rows at a time.
export const isSearchTerm = (term) => (term || "").trim().length >= 2;

// The Learners cell (D13, 002 D5): names come from users/{uid}/children, read
// per visible row — only when there is (or may be) something to read. A
// missing childCount is a student the backfill has not reached: read anyway.
export const needsChildren = (row) => row.childCount == null || row.childCount > 0;

// names: undefined = not read yet (fall back to the count), null = the read
// failed ("—"), array = the names.
export const learnersLabel = (row, names) => {
  if (names === null) return "—";
  const n = Number(row.childCount || 0);
  if (names) return names.length ? names.join(", ") : "Self only";
  if (n === 0) return "Self only";
  return `${n} ${n === 1 ? "child" : "children"}`;
};

export const statusChips = (row) =>
  [
    row.disabled && { label: "Disabled", color: "error" },
    row.hasFreeSession === true && { label: "Free session unused", color: "info" },
    row.isInternal === true && { label: "Internal", color: "default" },
  ].filter(Boolean);

// "Cancelled · Tue 8 Sep, 18:30" — the status first because that is the answer
// to "are they still with us" (D22), then the same time format the Lessons
// page uses. The word is sessionStatusChip's (047 D65): the last lesson has
// always started, so a Pending one reads "Awaiting outcome", never "Pending".
export const lastLessonLabel = (summary, { timeZone } = {}) => {
  const when = toDate(summary?.lastAt);
  if (!summary?.last || !when) return "—";
  const { status } = summary.last;
  const word = status ? sessionStatusChip({ status, startTimeUTC: summary.lastAt }).label : "Lesson";
  return `${word} · ${whenLabel(when, { timeZone })}`;
};

export const nextLessonLabel = (summary, { timeZone } = {}) => {
  const when = toDate(summary?.nextAt);
  if (!summary?.next || !when) return "—";
  return whenLabel(when, { timeZone });
};

export const emptyText = ({ filter = "", term = "" } = {}) => {
  if (isSearchTerm(term)) return `No student matches "${term.trim()}".`;
  const f = STUDENT_FILTERS.find((x) => x.key === filter);
  if (f && f.key) return `No students match ${f.label}.`;
  return "No students yet.";
};

// The list head's line under "Students" (047 D31, Page head 10620:2413). `total` counts the
// filter on screen and `all` every student, each null until its count lands or when it failed;
// `found` is a search's rows (null when no search is on). A search wins over the list, and a
// load that is running wins over the last failure (P39).
export const listSubtitle = ({
  filter = "",
  total = null,
  all = null,
  loading = false,
  error = false,
  searching = false,
  found = null,
  term = "",
  subject = "",
} = {}) => {
  if (searching) return "Searching…";
  if (found) {
    const n = found.length;
    const what = n === 0 ? "No students" : `First ${n} ${n === 1 ? "match" : "matches"}`;
    return `${what} for "${term.trim()}" · exact phone or name prefix`;
  }
  if (error && !loading) return "Could not load students";
  if (total === null) return loading ? "Loading students…" : "";
  const students = (n) => `${countText(n)} ${n === 1 ? "student" : "students"}`;
  if (subject) return `${students(total)} · ${subject}`;
  const f = STUDENT_FILTERS.find((x) => x.key === filter);
  if (!f || !f.key) return students(total);
  return all === null ? `${students(total)} · ${f.label}` : `${countText(total)} of ${students(all)} · ${f.label}`;
};

// The empty table (047 D34, Empty State 10661:11679): the title, a line on what to try, and
// which clear button to offer — "search", "filter", or none.
export const emptyState = ({ filter = "", term = "", subject = "" } = {}) => {
  if (isSearchTerm(term)) {
    return { title: emptyText({ term }), message: "Search matches an exact phone number or the start of a name.", clear: "search" };
  }
  if (subject) return { title: `No students chose ${subject}.`, message: 'Set Subject to "Any subject" to see every student.', clear: "subject" };
  const f = STUDENT_FILTERS.find((x) => x.key === filter);
  if (f && f.key) return { title: emptyText({ filter }), message: `Clear "${f.label}" to see every student.`, clear: "filter" };
  return { title: emptyText(), message: "Students appear here as soon as they sign up.", clear: null };
};

// ── Interests and the app a student runs (057 D14–D19) ──

// users/{uid}.interests: subject names. Missing, empty or not an array is "none"; stray blanks drop.
export const interestsOf = (student) =>
  Array.isArray(student?.interests) ? student.interests.filter((n) => typeof n === "string" && n.trim()).map((n) => n.trim()) : [];

// The table cell: the first `max` names and how many more ("+1").
export const chipOverflow = (list, max = 2) => ({
  shown: list.slice(0, max),
  more: Math.max(0, list.length - max),
});

const PLATFORMS = { ios: "iOS", android: "Android" };
const platformOf = (client) => PLATFORMS[client?.platform] || client?.platform || "";

// The table's App cell: "2.0.1 · iOS", "—" when the student has not opened a 2.0.x app.
export const appCell = (client) => (client?.appVersion ? [client.appVersion, platformOf(client)].filter(Boolean).join(" · ") : "—");

export const UNKNOWN_APP = "Unknown — older app";

// The page's App row: "2.0.1 (50) · iOS 18.2".
export const appRowText = (client) => {
  if (!client?.appVersion) return UNKNOWN_APP;
  const version = client.buildNumber ? `${client.appVersion} (${client.buildNumber})` : client.appVersion;
  return [version, [platformOf(client), client.osVersion].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
};

// The Update row: { text, title }. A store bundle has no updateId (or isEmbedded); an OTA names
// its channel and date, and the short id rides in the title.
export const updateText = (client) => {
  if (!client?.appVersion) return { text: UNKNOWN_APP, title: "" };
  if (client.isEmbedded || !client.updateId) return { text: "Store build", title: "" };
  const date = dateTimeLines(client.updateCreatedAt)?.date;
  return {
    text: ["OTA", client.channel, date].filter(Boolean).join(" · "),
    title: `Update ${String(client.updateId).slice(0, 8)}`,
  };
};

// Off when no device can receive a push or the student turned the setting off.
export const notificationsOn = (student) =>
  Array.isArray(student?.pushTokens) && student.pushTokens.length > 0 && student?.notificationPrefs?.push !== false;

export const ratingText = (student) => {
  const n = Number(student?.numberOfRatings) || 0;
  const avg = Number(student?.rating);
  if (!n || !Number.isFinite(avg)) return "—";
  return `${avg.toFixed(1)} · ${n} ${n === 1 ? "rating" : "ratings"}`;
};

export const stripeCustomerUrl = (id) => (id ? `https://dashboard.stripe.com/customers/${encodeURIComponent(id)}` : "");
